import { generateThemeColors } from "./color-generator";
import {
  generateMaterialThemePalettes,
  applyMaterialPalettesAsCSSVariables,
  exportMaterialPaletteForDebugging,
} from "./material-palette-generator";
import { applyMaterialThemeDynamically } from "./apply-material-dynamic-theme";

/**
 * Interface for session context stored in localStorage
 */
export interface SessionContext {
  agreement_id: number;
  buckets?: Array<{
    key: string;
    label: string;
  }>;
  default_bucket?: string;
  theme: {
    neutral: string;
    primary: string;
    secondary: string;
  };
}

/**
 * Apply dynamic theme colors to CSS variables
 * This function reads from localStorage and applies theme colors if available
 * Should be called before Angular bootstraps
 */
export function initializeDynamicTheme(): void {
  try {
    // Get session context from localStorage
    const sessionContextStr = localStorage.getItem("session_context");

    if (!sessionContextStr) {
      // No session context: restore Nexu default colors
      resetDynamicThemeVariables();
      return;
    }

    // Parse session context
    const sessionContext: SessionContext = JSON.parse(sessionContextStr);

    if (!sessionContext.theme) {
      // No theme in session context: restore Nexu default colors
      resetDynamicThemeVariables();
      return;
    }

    // Validate theme colors
    const { neutral, primary, secondary } = sessionContext.theme;
    if (!neutral || !primary || !secondary) {
      console.warn("Invalid theme colors in session_context, restoring defaults");
      resetDynamicThemeVariables();
      return;
    }

    // Generate all color shades from base colors
    const generatedColors = generateThemeColors({
      neutral,
      primary,
      secondary,
    });

    // Apply colors as CSS variables on :root
    const root = document.documentElement;

    Object.entries(generatedColors).forEach(([name, value]) => {
      root.style.setProperty(`--color-${name}`, value);
    });

    // Generate and apply Material Design palettes
    const materialPalettes = generateMaterialThemePalettes({
      neutral,
      primary,
      secondary,
    });

    applyMaterialPalettesAsCSSVariables(materialPalettes);

    // Apply Material theme CSS dynamically (injects <style> tag)
    applyMaterialThemeDynamically();

    // Store theme info for debugging
    if (typeof window !== "undefined") {
      (window as any).__nexuDynamicTheme = {
        applied: true,
        colors: generatedColors,
        materialPalettes: exportMaterialPaletteForDebugging(materialPalettes),
        source: sessionContext.theme,
      };
    }

    console.log("✓ Dynamic theme applied from session_context");
    console.log("✓ Material Design palettes generated and applied");
    console.log("✓ Material theme CSS injected dynamically");
  } catch (error) {
    console.error("Error initializing dynamic theme:", error);
    // Continue with default theme on error
  }
}

/**
 * Clear dynamic theme and restore defaults
 * Useful for testing or when session context is removed
 */
export function clearDynamicTheme(): void {
  // Remove session context
  localStorage.removeItem("session_context");

  // Remove all theme CSS variable overrides (restores Nexu defaults)
  resetDynamicThemeVariables();

  // Clean up debug info
  if (typeof window !== "undefined") {
    delete (window as any).__nexuDynamicTheme;
  }

  console.log("✓ Dynamic theme cleared, using defaults");
}

/**
 * Remove all theme CSS variable overrides from :root so the static Nexu
 * defaults (declared in SCSS) take effect again. Does not touch localStorage.
 */
export function resetDynamicThemeVariables(): void {
  // Remove CSS variable overrides
  const root = document.documentElement;
  const colorVars = [
    "base",
    "muted",
    "subtle",
    "primary-blue",
    "primaryshade-900",
    "primaryshade-700",
    "primaryshade-500",
    "primaryshade-300",
    "primaryshade-100",
    "secondary-blue",
    "secondaryshade-900",
    "secondaryshade-700",
    "secondaryshade-500",
    "secondaryshade-300",
    "secondaryshade-100",
    "gray-900",
    "gray-700",
    "gray-500",
    "gray-200",
    "gray-100",
  ];

  colorVars.forEach((varName) => {
    root.style.removeProperty(`--color-${varName}`);
  });

  // Remove Material Design palette variables
  const materialShades = [
    "50",
    "100",
    "200",
    "300",
    "400",
    "500",
    "600",
    "700",
    "800",
    "900",
    "A100",
    "A200",
    "A400",
    "A700",
  ];
  const materialPalettes = ["primary", "accent", "warn"];

  materialPalettes.forEach((palette) => {
    materialShades.forEach((shade) => {
      root.style.removeProperty(`--mat-${palette}-${shade}`);
      root.style.removeProperty(`--mat-${palette}-contrast-${shade}`);
    });
  });

  // Remove common Material variables
  [
    "primary",
    "primary-lighter",
    "primary-darker",
    "primary-contrast",
    "accent",
    "accent-lighter",
    "accent-darker",
    "accent-contrast",
    "warn",
    "warn-contrast",
  ].forEach((varName) => {
    root.style.removeProperty(`--${varName}`);
  });

  // Remove injected Material theme style tag
  const injectedStyle = document.getElementById("nexu-dynamic-material-theme");
  if (injectedStyle) {
    injectedStyle.remove();
    console.log("✓ Injected Material theme CSS removed");
  }
}

/**
 * Update dynamic theme with new colors
 * Useful for runtime theme updates
 */
export function updateDynamicTheme(theme: { neutral: string; primary: string; secondary: string }): void {
  // Update session context in localStorage
  const sessionContextStr = localStorage.getItem("session_context");
  let sessionContext: SessionContext;

  if (sessionContextStr) {
    sessionContext = JSON.parse(sessionContextStr);
    sessionContext.theme = theme;
  } else {
    sessionContext = {
      agreement_id: 0,
      theme,
    };
  }

  localStorage.setItem("session_context", JSON.stringify(sessionContext));

  // Reapply theme
  initializeDynamicTheme();
}
